/**
 * Typed client for the /auth API.
 *
 * Design:
 *   - Sessions ride in an httpOnly cookie, so there is intentionally no token
 *     here — page JS cannot read it (that's the XSS defence). `credentials:
 *     'same-origin'` lets the browser attach it.
 *   - Non-2xx responses THROW an `ApiError` carrying the server's stable machine
 *     `code`. That plays natively with TanStack Query (onError / error state)
 *     and keeps every call site uniform. The UI localizes `error.<code>`.
 *   - The server is always the authority; the client never trusts its own copy
 *     of a rule for anything but a pre-flight hint.
 */

export interface PublicUser {
  id: string;
  email: string;
  name: string;
  createdAt: number;
  /** Profile picture as a data: URL, or null for the initials fallback. */
  avatarUrl: string | null;
  /** 'admin' operates the platform; 'customer' only reaches their own bot. */
  role: 'admin' | 'customer';
}

/** Subscription/billing status for the current customer (Phase 4A-2). */
export type SubscriptionStatusValue =
  | 'none' | 'active' | 'trialing' | 'past_due' | 'canceled' | 'incomplete' | 'incomplete_expired' | 'unpaid';
export interface SubscriptionStatus {
  active: boolean;
  status: SubscriptionStatusValue;
  currentPeriodEnd: number | null;
  priceUsd: number;
  intervalDays: number;
  /** Free-trial length in days (0 = no trial). */
  trialDays: number;
  /** Whether this customer gets the free trial on checkout. */
  trialEligible: boolean;
  billingConfigured: boolean;
  /** Exempt operator (in OPERATOR_EMAILS) — full access without a subscription. */
  isOperator: boolean;
}

/** The customer's own bot instance (Phase 4B-1). */
export interface TenantStatus {
  hasTenant: boolean;
  tenantId?: string;
  status: string;
  poEmail?: string | null;
  canRun: boolean;
  reason: string;
}

/** Pocket Option connection state. 'verification_required' means PO challenged
 *  the login and the CUSTOMER must act — retrying cannot solve a CAPTCHA. */
export type LinkState =
  | 'not_provisioned' | 'credentials_needed' | 'linking'
  | 'linked' | 'verification_required' | 'failed';
export interface LinkStatus {
  state: LinkState;
  detail: string;
  poEmail: string | null;
  needsCustomer: boolean;
}

/**
 * Feature A — the verification relay.
 *
 * `where` is deliberately coarse rather than the page URL: a sign-in URL can
 * carry query tokens, and the client has no use for them.
 */
export type VerificationPhase = 'starting' | 'awaiting_user' | 'verifying' | 'verified' | 'closed';
export interface VerificationStatus {
  ok: boolean;
  phase: VerificationPhase;
  where: 'signin' | 'cabinet' | 'foreign' | 'unknown';
  msSinceStart: number;
  msRemaining: number;
  outcome: 'verified' | 'timeout' | 'aborted' | 'failed' | null;
  detail: string;
}
export interface VerificationFrame extends VerificationStatus {
  jpegBase64: string;
  width: number;
  height: number;
}

/** A structured API failure. `code` is the server's machine-readable reason. */
export class ApiError extends Error {
  constructor(
    public readonly code: string,
    public readonly status: number,
    public readonly detail?: string,
    /** The full parsed error body, for responses that carry structure (e.g. per-line errors). */
    public readonly body?: unknown
  ) {
    super(detail ?? code);
    this.name = 'ApiError';
  }
}


/**
 * ── Phase 4B-2: the customer's own bot, in detail ──────────────────────────
 * These mirror the bot's own responses. Everything is proxied through
 * /api/tenant/bot, which resolves the tenant from the SESSION, so a customer
 * physically cannot address anyone else's bot.
 */
export interface DailyStats {
  dayKey: string;
  netProfitUsd: number;
  totalTrades: number;
  wins: number;
  losses: number;
  ties: number;
  cycleWins: number;
  cycleLosses: number;
  cyclesCompleted: number;
  cyclesAbandoned: number;
}
export interface MartingaleSettings {
  enabled: boolean;
  multiplier: number;
  maxSteps: number;
  baseAmount: number;
}
export interface SafetySettings {
  dailyStopLossUsd: number;
  stopWinUsd: number;
}
export interface BotControl {
  running: boolean;
  pauseReason: string | null;
  accountType: 'demo' | 'real';
  activeAccountType: 'demo' | 'real';
  accountSwitchPending: boolean;
  payoutThresholdPct: number;
  martingale: MartingaleSettings;
  safety: SafetySettings;
  rateLimit: { tradesPerHour: number };
  rateLimitUsed: number;
  /**
   * PRO automatic mode's runtime toggle, or null when the bot has no candle
   * feed (the mode is not provisioned on this tenant at all).
   *
   * Null must render differently from false: one means "not available on this
   * bot", the other "available and switched off". A dead toggle for the first
   * case is worse than no toggle.
   */
  proAutoEnabled?: boolean | null;
}

/** One pair in PRO automatic mode's live analysis. */
export interface ProAsset {
  asset: string;
  bars: number;
  trend: string;
  ready: boolean;
  ageMs: number | null;
  payoutPct?: number | null;
  isActive?: boolean | null;
  /** Why this pair would be skipped right now; null when it is eligible. */
  skipReason?: string | null;
}

export interface ProStatus {
  /** This bot HAS the mode (a candle feed exists). Not the runtime toggle. */
  enabled: boolean;
  /** Whether the selector is currently choosing the pair. */
  selecting?: boolean;
  assets?: ProAsset[];
  framesAccepted?: number;
  cyclesByAsset?: Record<string, number>;
  windowBars?: number;
  /** The SELECTOR's payout floor: below this a pair is not chosen. */
  minPayoutPct?: number;
  /**
   * The EXECUTION guard's floor: a different, stacking gate. The selector
   * picks at >= minPayoutPct, then the guard re-reads the live payout and
   * refuses the click below this. On the client's tenant they differ (85/80).
   */
  executionThresholdPct?: number;
  readyCount?: number;
  eligibleCount?: number;
}
export interface BotState {
  step: number;
  nextStake: number;
  consecutiveLosses: number;
  martingaleEnabled: boolean;
  multiplier: number;
  maxSteps: number;
  balance?: string | null;
  daily: DailyStats;
  halted: boolean;
  haltReason: string | null;
  control: BotControl;
  historySummary?: { count: number; wins: number; losses: number; ties: number; netProfitUsd: number };
}
/**
 * Mirrors the engine's own TradeRecord field-for-field.
 *
 * The names matter: the engine emits `timestamp` and `actualStake`, not `ts`
 * and `stake`. Guessing them produced a History screen with blank dates and an
 * admin equity curve pinned flat at zero, because every record silently failed
 * to bucket. Keep these aligned with src/core/history.ts.
 */
export interface TradeRecord {
  id: string;
  timestamp: number;
  asset: string;
  direction: 'BUY' | 'SELL';
  requestedStake: number | null;
  actualStake: number;
  outcome: 'WIN' | 'LOSS' | 'TIE' | 'UNKNOWN' | 'SKIPPED';
  profitUsd: number;
  martingaleStep: number;
  expirySeconds: number;
  payoutPct: number | null;
  latencyMs: number;
  isOverride: boolean;
  skipReason?: string;
  detail?: string;
}
export interface HistoryResponse { filter: string; count: number; items: TradeRecord[] }
/**
 * Mirrors src/signal/scheduler.ts QueueItem EXACTLY. The previous shape here
 * invented a `result` object that the bot never sends and typed scheduledFor as
 * always-a-number; it is null for a signal that was already stale at intake,
 * and `new Date(null)` is the Unix epoch — which is how skipped rows came to
 * show a nonsense time on the customer's phone.
 */
export interface QueueItem {
  id: string;
  createdAt: number;
  /** Absolute UTC ms, or null if it was already past when parsed. */
  scheduledFor: number | null;
  firedAt: number | null;
  resolvedAt: number | null;
  status: 'pending' | 'executing' | 'completed' | 'skipped' | 'cancelled' | 'unknown';
  signal: {
    rawLine: string;
    lineNumber: number;
    asset: string;
    pair: string;
    market: 'otc' | 'normal';
    direction: 'BUY' | 'SELL';
    timeframe: string;
    expirySeconds: number;
    /** "HH:MM" as the bot received it, i.e. UTC. */
    entryTime: string;
    entryHour: number;
    entryMinute: number;
  };
  /** Trade #0 outcome, if any. */
  outcome?: string;
  profitUsd?: number;
  /** Reason when skipped/cancelled; result line when completed. */
  detail?: string;
  /** Cycle-level outcome; preferred over `outcome` for the badge. */
  cycleOutcome?: 'WIN' | 'LOSS_MAX' | 'ABANDONED' | 'TIE';
  cycleProfitUsd?: number;
}
export interface QueueSummary {
  total: number; pending: number; executing: number; completed: number;
  skipped: number; cancelled: number; unknown: number;
}
export interface QueueResponse { summary: QueueSummary; items: QueueItem[] }
export interface SignalResult {
  ok: boolean; detail: string; latencyMs?: number; skipped?: boolean;
  state?: BotState; payoutPct?: number | null;
}
/** Wire shape of POST /signals/batch (src/signal/webhook.ts): `queued` is the
 *  full QueueItem[] — including rows the scheduler marked skipped at intake,
 *  which have scheduledFor null — and `errors` are the parser's ParseError[]. */
export interface BatchResult {
  ok: boolean;
  queuedCount: number; errorCount: number; totalLines: number;
  queued: QueueItem[];
  errors: Array<{ lineNumber: number; rawLine: string; reason: string }>;
}
export interface BotHealth { ok: boolean; sessionOk: boolean; halted: boolean; uptimeSec: number | null; secsSinceHealthy: number | null; alertChannels: string[] }
export interface AlertsResponse { health?: Partial<BotHealth>; alerts?: Array<{ ts: number; level: string; message: string }> }


/** ── Phase 4B-2: admin (operator only; every route 404s for a customer) ──── */
export interface AdminOverview {
  ok: true;
  tenants: number;
  running: number;
  stopped: number;
  byStatus: Record<string, number>;
  price: { usd: number; configured: boolean };
}
export interface AdminCustomer {
  tenantId: string;
  port: number;
  status: string;
  poEmail: string | null;
  note: string | null;
  createdAt: number;
  user: { id: string; email: string; name: string; role: string } | null;
  subscription: {
    active: boolean;
    status: string;
    currentPeriodEnd: number | null;
    /** Access granted by an operator rather than bought through Stripe. */
    complimentary: boolean;
  };
  running: boolean;
  canRun: boolean;
  reason: string;
  restartsPaused: boolean;
}


export interface AdminAnalytics {
  ok: true;
  days: number;
  series: Array<{ date: string; net: number; trades: number; wins: number; losses: number }>;
  cumulative: Array<{ date: string; net: number }>;
  perCustomer: Array<{
    tenantId: string;
    email: string;
    poEmail: string | null;
    reachable: boolean;
    balance: string | null;
    halted: boolean;
    step: number;
    today: { net: number; trades: number; wins: number; losses: number; cycleWinRate: number | null };
    lifetime: { net: number; trades: number };
  }>;
  totals: {
    net: number; trades: number; wins: number; losses: number;
    todayNet: number; activeSubs: number; mrrUsd: number;
  };
}

export interface AdminSystem {
  ok: true;
  cores: number;
  loadAvg: { m1: number; m5: number; m15: number };
  memory: { totalMb: number; usedMb: number; pct: number };
  uptimeSec: number;
  capacity: { perBotCores: number; maxBots: number; used: number };
  perBot: Array<{ tenantId: string; memoryMb: number | null; cpuUsecTotal: number | null }>;
}
export interface AdminActivityItem {
  tenantId: string; email: string; timestamp: number; asset: string;
  direction: 'BUY' | 'SELL'; outcome: string; profitUsd: number;
  actualStake: number; martingaleStep: number;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      ...init,
    });
  } catch {
    // Network / offline / DNS — no HTTP status at all.
    throw new ApiError('network', 0);
  }

  let body: unknown = undefined;
  const text = await res.text().catch(() => '');
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      /* non-JSON body — leave undefined */
    }
  }

  if (!res.ok) {
    const b = (body ?? {}) as { error?: string; detail?: string };
    throw new ApiError(b.error ?? 'unknown', res.status, b.detail, body);
  }
  return body as T;
}

const post = <T>(path: string, payload?: unknown): Promise<T> =>
  request<T>(path, { method: 'POST', body: JSON.stringify(payload ?? {}) });

/** Deletions (clear history, clear queue, cancel one queued signal). */
const del = <T>(path: string): Promise<T> => request<T>(path, { method: 'DELETE' });

export const api = {
  me: () => request<{ ok: true; user: PublicUser }>('/auth/me'),

  // Sign-up no longer logs in — the account is unverified (hard email gate), so
  // the server issues no session and returns only a confirmation detail.
  signup: (input: { name: string; email: string; password: string }) =>
    post<{ ok: true; detail: string }>('/auth/signup', input),

  login: (input: { email: string; password: string }) =>
    post<{ ok: true; user: PublicUser }>('/auth/login', input),

  verifyEmail: (input: { token: string }) =>
    post<{ ok: true; detail: string }>('/auth/verify', input),

  resendVerification: (input: { email: string }) =>
    post<{ ok: true; detail: string }>('/auth/verify/resend', input),

  logout: () => post<{ ok: true }>('/auth/logout'),

  updateProfile: (patch: { name?: string; email?: string; avatarUrl?: string | null }) =>
    request<{ ok: true; user: PublicUser; emailVerificationSent?: boolean }>('/auth/profile', {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  changePassword: (input: { currentPassword: string; newPassword: string }) =>
    post<{ ok: true; user: PublicUser }>('/auth/password/change', input),

  forgotPassword: (input: { email: string }) =>
    post<{ ok: true; detail: string }>('/auth/password/forgot', input),

  resetPassword: (input: { token: string; password: string }) =>
    post<{ ok: true; detail: string }>('/auth/password/reset', input),

  // ── Phase 4B-1: the customer's own bot ─────────────────────────────────────
  tenantStatus: () => request<{ ok: true } & TenantStatus>('/api/tenant/status'),
  provisionBot: () => post<{ ok: true; tenantId: string; status: string; detail: string }>('/api/tenant/provision'),
  linkStatus: () => request<{ ok: true } & LinkStatus>('/api/tenant/link'),
  linkAccount: (input: { poEmail: string; poPassword: string; accountType: 'demo' | 'real' }) =>
    post<{ ok: boolean } & LinkStatus>('/api/tenant/link', input),
  unlinkAccount: () => post<{ ok: true } & LinkStatus>('/api/tenant/unlink'),


  // ── Phase 4B-2: the customer's own bot, full control surface ───────────────
  // Every path goes through /api/tenant/bot so the tenant comes from the
  // session. Writes additionally require an active subscription server-side.
  botState: () => request<BotState>('/api/tenant/bot/state'),
  botAlerts: () => request<AlertsResponse>('/api/tenant/bot/alerts'),
  botHistory: (filter = 'today', limit = 200) =>
    request<HistoryResponse>(
      `/api/tenant/bot/history?filter=${encodeURIComponent(filter)}&limit=${limit}`
    ),
  botQueue: () => request<QueueResponse>('/api/tenant/bot/signals/queue'),

  botSignal: (input: { asset: string; direction: 'BUY' | 'SELL'; expirySeconds: number; amount?: number; martingaleEnabled?: boolean }) =>
    post<SignalResult>('/api/tenant/bot/signal', input),
  botQueueBatch: (text: string) => post<BatchResult>('/api/tenant/bot/signals/batch', { text }),

  botPause: () => post<{ ok: true; state: BotState }>('/api/tenant/bot/control/pause'),
  botResume: () => post<{ ok: true; state: BotState }>('/api/tenant/bot/control/resume'),
  botSetPayout: (pct: number) => post<{ ok: true; state: BotState }>('/api/tenant/bot/control/payout', { pct }),
  /** PRO automatic mode's live analysis. 386 bytes measured, so safe to poll. */
  botProStatus: () => request<ProStatus>('/api/tenant/bot/pro-status'),
  botSetProAuto: (enabled: boolean) =>
    post<{ ok: true; state: BotState }>('/api/tenant/bot/control/pro-auto', { enabled }),
  botSetMartingale: (patch: Partial<MartingaleSettings>) =>
    post<{ ok: true; state: BotState }>('/api/tenant/bot/control/martingale', patch),
  botSetSafety: (patch: Partial<SafetySettings>) =>
    post<{ ok: true; state: BotState }>('/api/tenant/bot/control/safety', patch),
  botSetRateLimit: (patch: { tradesPerHour: number }) =>
    post<{ ok: true; state: BotState }>('/api/tenant/bot/control/rate-limit', patch),
  botSwitchAccount: (type: 'demo' | 'real') =>
    post<{ ok: boolean; state: BotState; detail: string }>('/api/tenant/bot/control/account', { type, confirm: true }),

  botResetLadder: () => post<{ ok: true; state: BotState }>('/api/tenant/bot/reset'),
  botResetDay: () => post<{ ok: true; state: BotState }>('/api/tenant/bot/reset/day'),
  botClearHistory: () => del<{ ok: true }>('/api/tenant/bot/history'),
  botClearQueue: () => del<{ ok: true }>('/api/tenant/bot/signals/queue'),
  botCancelQueued: (id: string) =>
    del<{ ok: true }>(`/api/tenant/bot/signals/${encodeURIComponent(id)}`),


  // ── Phase 4B-2: admin ──────────────────────────────────────────────────────
  adminOverview: () => request<AdminOverview>('/api/admin/overview'),
  adminAnalytics: (days = 14) => request<AdminAnalytics>('/api/admin/analytics?days=' + days),
  adminSystem: () => request<AdminSystem>('/api/admin/system'),
  adminActivity: (limit = 40) => request<{ ok: true; items: AdminActivityItem[] }>('/api/admin/activity?limit=' + limit),
  adminCustomers: () => request<{ ok: true; customers: AdminCustomer[] }>('/api/admin/customers'),
  adminAction: (id: string, action: 'start' | 'stop' | 'restart') =>
    post<{ ok: boolean; detail: string }>('/api/admin/tenant/' + encodeURIComponent(id) + '/' + action),
  adminSuspend: (id: string, suspend: boolean) =>
    post<{ ok: true; status: string }>('/api/admin/tenant/' + encodeURIComponent(id) + '/suspension', { suspend }),

  // Complimentary access is addressed by USER id, not tenant id: the grant is a
  // property of the account, so it can be granted before a bot is ever linked.
  adminGrantComplimentary: (userId: string, reason: string, expiresAt?: number | null) =>
    post<{ ok: true }>('/api/admin/complimentary/' + encodeURIComponent(userId), {
      reason,
      expiresAt: expiresAt ?? null,
    }),
  adminRevokeComplimentary: (userId: string) =>
    del<{ ok: true }>('/api/admin/complimentary/' + encodeURIComponent(userId)),

  // ── Feature A: the verification relay ──────────────────────────────────────
  // The customer path takes no tenant id — the gateway resolves it from the
  // session. `base` exists only so the Admin Panel can point the same component
  // at /api/admin/tenant/<id>/verify.
  verifyStart: (base = '/api/tenant/verify') => post<VerificationStatus>(base + '/start'),
  verifyStatus: (base = '/api/tenant/verify') => request<VerificationStatus>(base + '/status'),
  verifyFrame: (base = '/api/tenant/verify') => request<VerificationFrame>(base + '/frame'),
  verifyClick: (p: { x: number; y: number; w: number; h: number }, base = '/api/tenant/verify') =>
    post<VerificationStatus>(base + '/click', p),
  verifyKey: (text: string, base = '/api/tenant/verify') => post<VerificationStatus>(base + '/key', { text }),
  verifyFinish: (base = '/api/tenant/verify') => post<VerificationStatus>(base + '/finish'),

  // ── Phase 4A-2: billing ────────────────────────────────────────────────────
  subscriptionStatus: () => request<{ ok: true } & SubscriptionStatus>('/billing/status'),
  createCheckout: () => post<{ ok: true; url: string }>('/billing/checkout'),
  billingPortal: () => post<{ ok: true; url: string }>('/billing/portal'),
};
